import type {
  HealthConcern,
  SocialHistoryItem,
  FunctionalStatus,
  Encounter,
  VitalSign,
  ClinicalNote,
} from "@/shared/types";

export const sampleHealthConcerns: HealthConcern[] = [
  {
    id: "hc-1",
    concern: "Recurring lower back pain",
    category: "symptom",
    severity: "moderate",
    status: "active",
    onsetDate: "2024-06-15",
    lastUpdated: "2025-03-10",
    notes: "Pain worsens with prolonged sitting. Physical therapy recommended.",
    relatedDiagnoses: ["Lumbar strain", "Mild degenerative disc disease"],
    actionItems: ["Continue PT exercises daily", "Use lumbar support cushion", "Schedule follow-up if pain worsens"],
  },
  {
    id: "hc-2",
    concern: "Elevated cholesterol levels",
    category: "condition",
    severity: "mild",
    status: "monitoring",
    onsetDate: "2024-01-20",
    lastUpdated: "2025-02-15",
    notes: "LDL slightly elevated at 142 mg/dL. Dietary modifications initiated.",
    relatedDiagnoses: ["Hyperlipidemia"],
    actionItems: ["Follow low-fat diet", "Increase fiber intake", "Recheck lipid panel in 3 months"],
  },
  {
    id: "hc-3",
    concern: "Annual skin cancer screening",
    category: "preventive",
    severity: "mild",
    status: "active",
    onsetDate: "2025-01-01",
    lastUpdated: "2025-01-15",
    notes: "Due for annual dermatology exam. Previous history of sun exposure.",
    actionItems: ["Schedule dermatology appointment", "Use SPF 30+ daily"],
  },
  {
    id: "hc-4",
    concern: "Work-related stress and anxiety",
    category: "mental_health",
    severity: "moderate",
    status: "active",
    onsetDate: "2024-09-01",
    lastUpdated: "2025-03-01",
    notes: "Experiencing increased anxiety related to job demands. Sleep occasionally affected.",
    actionItems: ["Practice mindfulness exercises", "Consider counseling referral", "Maintain regular exercise routine"],
  },
  {
    id: "hc-5",
    concern: "Weight management",
    category: "lifestyle",
    severity: "mild",
    status: "active",
    onsetDate: "2024-03-01",
    lastUpdated: "2025-03-15",
    notes: "BMI currently 27.2. Goal to reach healthy weight range through diet and exercise.",
    actionItems: ["Track daily caloric intake", "Exercise 150 min/week", "Monthly weight check-ins"],
  },
];

export const sampleSocialHistory: SocialHistoryItem[] = [
  {
    id: "sh-1",
    category: "tobacco",
    status: "Former smoker",
    details: "Smoked 1 pack per day for 10 years. Quit in 2018.",
    startDate: "2008-01-01",
    endDate: "2018-06-15",
    lastUpdated: "2025-01-10",
  },
  {
    id: "sh-2",
    category: "alcohol",
    status: "Social drinker",
    details: "1-2 drinks per week, typically wine with dinner on weekends.",
    frequency: "1-2 drinks/week",
    lastUpdated: "2025-01-10",
  },
  {
    id: "sh-3",
    category: "exercise",
    status: "Moderately active",
    details: "Walks 30 minutes 4x/week. Occasional swimming.",
    frequency: "4 times per week",
    lastUpdated: "2025-03-01",
  },
  {
    id: "sh-4",
    category: "diet",
    status: "Working on improvement",
    details: "Trying to reduce processed foods and increase vegetables. Following Mediterranean diet loosely.",
    lastUpdated: "2025-02-15",
  },
  {
    id: "sh-5",
    category: "occupation",
    status: "Employed full-time",
    details: "Office work, primarily desk-based. 40-50 hours per week. Moderate stress level.",
    startDate: "2019-04-01",
    lastUpdated: "2025-01-10",
  },
  {
    id: "sh-6",
    category: "living_situation",
    status: "Stable housing",
    details: "Lives with spouse and 2 children in owned home. Safe neighborhood.",
    lastUpdated: "2025-01-10",
  },
  {
    id: "sh-7",
    category: "stress",
    status: "Moderate",
    details: "Primary stressors include work deadlines and family responsibilities. Using exercise and meditation to manage.",
    lastUpdated: "2025-03-01",
  },
];

export const sampleFunctionalStatus: FunctionalStatus[] = [
  {
    id: "fs-1",
    category: "mobility",
    assessment: "Able to walk independently without assistive devices. Occasional lower back discomfort after prolonged walking.",
    score: 90,
    maxScore: 100,
    status: "independent",
    limitations: ["Difficulty with prolonged standing (>30 min)", "Mild discomfort climbing stairs"],
    goals: ["Improve core strength", "Increase walking endurance"],
    assessedDate: "2025-02-15",
    assessedBy: "Dr. Michael Rodriguez",
  },
  {
    id: "fs-2",
    category: "self_care",
    assessment: "Fully independent in all activities of daily living including bathing, dressing, grooming, and toileting.",
    score: 100,
    maxScore: 100,
    status: "independent",
    assessedDate: "2025-02-15",
    assessedBy: "Dr. Michael Rodriguez",
  },
  {
    id: "fs-3",
    category: "cognition",
    assessment: "Alert and oriented. No memory concerns reported. Able to manage finances and medications independently.",
    score: 28,
    maxScore: 30,
    status: "independent",
    assessedDate: "2025-01-10",
    assessedBy: "Dr. Sarah Chen",
  },
  {
    id: "fs-4",
    category: "vision",
    assessment: "Corrected vision 20/25 with glasses. Reading glasses required for close work.",
    status: "modified_independent",
    assistiveDevices: ["Prescription eyeglasses", "Reading glasses"],
    assessedDate: "2024-09-20",
    assessedBy: "Dr. Linda Park, OD",
  },
  {
    id: "fs-5",
    category: "hearing",
    assessment: "Mild high-frequency hearing loss in right ear. No hearing aids currently.",
    status: "modified_independent",
    limitations: ["Difficulty hearing in noisy environments"],
    goals: ["Consider hearing evaluation for possible hearing aid"],
    assessedDate: "2024-06-15",
    assessedBy: "Dr. James Wilson, Audiologist",
  },
];

export const sampleEncounters: Encounter[] = [
  {
    id: "enc-1",
    type: "office_visit",
    date: "2025-03-15",
    time: "10:30 AM",
    provider: "Dr. Sarah Chen",
    providerSpecialty: "Internal Medicine",
    facility: "Bay Area Medical Center",
    reasonForVisit: "Annual physical examination",
    chiefComplaint: "Routine checkup, no acute concerns",
    diagnosis: ["Essential hypertension, controlled", "Type 2 diabetes, controlled", "Hyperlipidemia"],
    procedures: ["Comprehensive metabolic panel", "Lipid panel", "HbA1c"],
    followUp: "Return in 3 months for lab review",
    notes: "Patient doing well overall. Continue current medications. Encouraged to increase physical activity.",
  },
  {
    id: "enc-2",
    type: "telehealth",
    date: "2025-02-20",
    time: "2:00 PM",
    provider: "Dr. Sarah Chen",
    providerSpecialty: "Internal Medicine",
    facility: "Bay Area Medical Center - Virtual",
    reasonForVisit: "Medication refill and blood pressure follow-up",
    chiefComplaint: "Home BP readings slightly elevated",
    diagnosis: ["Essential hypertension"],
    followUp: "Continue monitoring BP at home, call if readings exceed 150/95",
    notes: "Reviewed home BP log. Readings averaging 135/85. Discussed sodium reduction strategies.",
  },
  {
    id: "enc-3",
    type: "office_visit",
    date: "2025-01-15",
    time: "9:00 AM",
    provider: "Dr. Michael Rodriguez",
    providerSpecialty: "Orthopedics",
    facility: "Bay Area Orthopedic Specialists",
    reasonForVisit: "Lower back pain follow-up",
    chiefComplaint: "Persistent lower back pain, improving with PT",
    diagnosis: ["Lumbar strain", "Mild degenerative disc disease L4-L5"],
    procedures: ["Physical examination", "Range of motion assessment"],
    followUp: "Return in 6 weeks, continue PT",
    notes: "Good progress with physical therapy. Core strengthening exercises helping. MRI not indicated at this time.",
  },
  {
    id: "enc-4",
    type: "lab_only",
    date: "2025-03-10",
    time: "8:00 AM",
    provider: "Lab Services",
    providerSpecialty: "Laboratory",
    facility: "Bay Area Medical Center Lab",
    reasonForVisit: "Fasting blood work",
    procedures: ["CBC", "CMP", "Lipid panel", "HbA1c", "TSH"],
    notes: "Fasting labs drawn. Results will be reviewed at upcoming appointment.",
  },
  {
    id: "enc-5",
    type: "emergency",
    date: "2024-08-22",
    time: "7:45 PM",
    provider: "Dr. Amanda Lee",
    providerSpecialty: "Emergency Medicine",
    facility: "Bay Area Medical Center ER",
    reasonForVisit: "Acute lower back pain",
    chiefComplaint: "Sudden onset severe lower back pain after lifting",
    diagnosis: ["Acute lumbar strain"],
    procedures: ["X-ray lumbar spine", "IV pain medication"],
    followUp: "Follow up with orthopedics in 1 week",
    notes: "X-ray negative for fracture. Discharged with pain medication and muscle relaxant. Ice, rest advised.",
  },
];

export const sampleVitalSigns: VitalSign[] = [
  {
    id: "vs-1",
    date: "2025-03-15",
    time: "10:30 AM",
    measuredBy: "Nurse Johnson",
    bloodPressure: { systolic: 132, diastolic: 84, status: "normal" },
    heartRate: { value: 72, status: "normal" },
    temperature: { value: 98.4, unit: "F", status: "normal" },
    respiratoryRate: { value: 16, status: "normal" },
    oxygenSaturation: { value: 98, status: "normal" },
    weight: { value: 185, unit: "lbs" },
    height: { value: 70, unit: "in" },
    bmi: { value: 26.5, category: "overweight" },
  },
  {
    id: "vs-2",
    date: "2025-02-15",
    time: "2:15 PM",
    measuredBy: "Self-reported",
    bloodPressure: { systolic: 138, diastolic: 88, status: "elevated" },
    heartRate: { value: 78, status: "normal" },
    weight: { value: 187, unit: "lbs" },
  },
  {
    id: "vs-3",
    date: "2025-01-15",
    time: "9:00 AM",
    measuredBy: "Nurse Martinez",
    bloodPressure: { systolic: 128, diastolic: 82, status: "normal" },
    heartRate: { value: 70, status: "normal" },
    temperature: { value: 98.2, unit: "F", status: "normal" },
    respiratoryRate: { value: 14, status: "normal" },
    oxygenSaturation: { value: 99, status: "normal" },
    weight: { value: 186, unit: "lbs" },
    height: { value: 70, unit: "in" },
    bmi: { value: 26.7, category: "overweight" },
  },
  {
    id: "vs-4",
    date: "2024-12-10",
    time: "11:00 AM",
    measuredBy: "Nurse Johnson",
    bloodPressure: { systolic: 142, diastolic: 90, status: "high" },
    heartRate: { value: 82, status: "normal" },
    temperature: { value: 98.6, unit: "F", status: "normal" },
    weight: { value: 189, unit: "lbs" },
    bmi: { value: 27.1, category: "overweight" },
  },
  {
    id: "vs-5",
    date: "2024-09-20",
    time: "3:30 PM",
    measuredBy: "Nurse Williams",
    bloodPressure: { systolic: 130, diastolic: 85, status: "normal" },
    heartRate: { value: 74, status: "normal" },
    weight: { value: 190, unit: "lbs" },
    height: { value: 70, unit: "in" },
    bmi: { value: 27.2, category: "overweight" },
  },
];

export const sampleClinicalNotes: ClinicalNote[] = [
  {
    id: "cn-1",
    date: "2025-03-15",
    type: "progress_note",
    provider: "Dr. Sarah Chen",
    providerCredentials: "MD, FACP",
    specialty: "Internal Medicine",
    encounterId: "enc-1",
    subjective: "Patient presents for annual physical. Reports feeling well overall. Denies chest pain, shortness of breath, or palpitations. Lower back pain has improved with physical therapy. Sleep is adequate. Stress level moderate due to work.",
    objective: "Vitals: BP 132/84, HR 72, Temp 98.4°F, RR 16, SpO2 98%, Wt 185 lbs, Ht 70 in, BMI 26.5. General: Well-appearing, in no acute distress. HEENT: Normocephalic, PERRLA, oropharynx clear. Cardiovascular: RRR, no murmurs. Lungs: Clear to auscultation bilaterally. Abdomen: Soft, non-tender, no organomegaly. Extremities: No edema, pulses 2+ bilaterally.",
    assessment: "1. Essential hypertension - controlled on current regimen\n2. Type 2 diabetes mellitus - HbA1c pending, last was 6.8%\n3. Hyperlipidemia - on statin therapy\n4. Lumbar strain - improving with PT\n5. Overweight - BMI 26.5",
    plan: "1. Continue Lisinopril 10mg daily\n2. Continue Metformin 500mg BID\n3. Continue Atorvastatin 20mg at bedtime\n4. Labs ordered: CMP, lipid panel, HbA1c, CBC\n5. Continue PT for lower back\n6. Lifestyle counseling provided re: diet and exercise\n7. RTC 3 months or PRN",
    content: "Annual physical examination completed. Patient is doing well with chronic conditions well-controlled. Emphasis on continued lifestyle modifications for weight and cholesterol management.",
    diagnoses: ["Essential hypertension", "Type 2 diabetes mellitus", "Hyperlipidemia", "Lumbar strain"],
    signedDate: "2025-03-15",
  },
  {
    id: "cn-2",
    date: "2025-01-15",
    type: "consultation",
    provider: "Dr. Michael Rodriguez",
    providerCredentials: "MD",
    specialty: "Orthopedic Surgery",
    encounterId: "enc-3",
    subjective: "Follow-up for lower back pain. Patient reports improvement since starting physical therapy. Pain now 3/10 at worst, down from 7/10 initially. Able to sit for longer periods. Still occasional stiffness in mornings.",
    objective: "Lumbar spine: Mild paraspinal tenderness L4-L5 region. ROM: Flexion 80°, extension 20°, lateral bending symmetric. SLR negative bilaterally. Strength 5/5 bilateral lower extremities. DTRs 2+ and symmetric. Sensation intact.",
    assessment: "Lumbar strain with underlying mild degenerative disc disease L4-L5, improving with conservative management.",
    plan: "1. Continue physical therapy 2x/week for 4 more weeks\n2. Home exercise program daily\n3. Ibuprofen PRN for flares\n4. Ergonomic workstation assessment recommended\n5. Return in 6 weeks\n6. MRI not indicated currently given clinical improvement",
    content: "Good progress with conservative management of lumbar strain. Patient motivated and compliant with PT.",
    diagnoses: ["Lumbar strain", "Degenerative disc disease L4-L5"],
    signedDate: "2025-01-15",
  },
  {
    id: "cn-3",
    date: "2024-08-22",
    type: "discharge_summary",
    provider: "Dr. Amanda Lee",
    providerCredentials: "MD",
    specialty: "Emergency Medicine",
    encounterId: "enc-5",
    subjective: "48-year-old male presents with acute onset severe lower back pain after lifting a heavy box at home approximately 4 hours ago. Pain is sharp, localized to lower back, radiates slightly to left buttock. Denies leg weakness, numbness, or bowel/bladder changes.",
    objective: "Lumbar spine X-ray: No fracture or dislocation. Normal vertebral alignment. Mild disc space narrowing L4-L5. No acute bony abnormality.",
    assessment: "Acute lumbar strain, likely muscular in nature. No red flags for serious pathology.",
    plan: "1. Discharge home with:\n   - Cyclobenzaprine 10mg TID PRN muscle spasm x 5 days\n   - Ibuprofen 600mg TID with food x 7 days\n2. Ice 20 min every 2-3 hours for 48 hours, then heat\n3. Avoid heavy lifting\n4. Follow up with orthopedics in 1 week if not improving\n5. Return to ER for leg weakness, numbness, or bladder changes",
    content: "Patient discharged in stable condition with pain controlled. Precautions reviewed and understood.",
    diagnoses: ["Acute lumbar strain"],
    signedDate: "2024-08-22",
  },
];
